import { useEffect, useRef } from "react";
import { PhoneMissed } from "lucide-react";
import type { Callback } from "./types";
import { CallbackCard } from "./callback-card";

interface CallbacksListProps {
  callbacks: Callback[];
  expandedId: string | null;
  selectedIds: Set<string>;
  isFiltered: boolean;
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  onClearFilters: () => void;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onPauseResume: (id: string) => void;
  onReschedule: (id: string) => void;
}

export function CallbacksList(p: CallbacksListProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { hasMore, loading, onLoadMore } = p;

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore || loading) return;
    const obs = new IntersectionObserver(
      (entries) => entries[0].isIntersecting && onLoadMore(),
      { rootMargin: "200px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loading, onLoadMore]);

  if (p.callbacks.length === 0) {
    return (
      <div className="rounded-xl border-2 border-dashed border-line p-12 text-center">
        <PhoneMissed className="mx-auto h-10 w-10 text-line" />
        <p className="mt-4 text-lg font-bold text-ink">No callbacks found</p>
        
        {p.isFiltered && (
          <button
            onClick={p.onClearFilters}
            className="mt-4 rounded-lg border border-line bg-elevated px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-soft"
          >
            Clear Filters
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {p.callbacks.map((cb) => (
        <CallbackCard
          key={cb.id}
          callback={cb}
          expanded={p.expandedId === cb.id}
          selected={p.selectedIds.has(cb.id)}
          onToggleExpand={() => p.onToggleExpand(cb.id)}
          onToggleSelect={() => p.onToggleSelect(cb.id)}
          onPauseResume={() => p.onPauseResume(cb.id)}
          onReschedule={() => p.onReschedule(cb.id)}
        />
      ))}

      {p.hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          {p.loading && (
            <span className="font-mono text-xs text-ink-3">Loading more callbacks…</span>
          )}
        </div>
      )}
    </div>
  );
}
